import React, { useMemo, useState, useRef, useEffect } from 'react';
import { usePTS } from '../../context/PTSContext';

const CAT_CONFIG = [
  { key: 'Rakennetekniikka',  color: '#2a7a2a', label: 'Rakennetekniikka' },
  { key: 'LVI Järjestelmät',  color: '#1a6db5', label: 'LVI' },
  { key: 'Sähköjärjestelmät', color: '#c47a00', label: 'Sähkö' },
  { key: 'Lisätutkimukset',   color: '#7b3fa0', label: 'Tutkimus' },
];
const CAT_COLOR = Object.fromEntries(CAT_CONFIG.map(c => [c.key, c.color]));

const SEG_W_MAX = 160;
const SVG_H    = 360;
const DOT_R    = 11;
const Y_TOP    = 55;
const Y_BOT    = 280;
const MARGIN_L = 72;
const MARGIN_R = 56;
const TT_W   = 300;
const TT_ROW = 64;
const TT_HDR = 60;
const TT_PAD = 24;

function fmt(n) {
  if (!n) return '–';
  return (n * 1000).toLocaleString('fi-FI', { maximumFractionDigits: 0 }) + ' €';
}
function fmtShort(n) {
  if (!n) return '–';
  const e = n * 1000;
  if (e >= 100_000) return Math.round(e / 1000) + ' k€';
  return e.toLocaleString('fi-FI', { maximumFractionDigits: 0 }) + ' €';
}

function niceTicks(maxVal, targetCount = 4) {
  if (!maxVal) return [0];
  const rawStep = maxVal / targetCount;
  const exp  = Math.floor(Math.log10(rawStep));
  const mag  = Math.pow(10, exp);
  const norm = rawStep / mag;
  const step = norm <= 1.5 ? mag : norm <= 3.5 ? 2 * mag : norm <= 7.5 ? 5 * mag : 10 * mag;
  const ticks = [];
  for (let v = step; v <= maxVal * 1.01; v += step) {
    ticks.push(Math.round(v / step) * step);
  }
  return ticks;
}

function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], c = pts[i];
    const mx = (p.x + c.x) / 2;
    d += ` C ${mx} ${p.y} ${mx} ${c.y} ${c.x} ${c.y}`;
  }
  return d;
}

export default function PTSRoadmap() {
  const {
    years, currentYear,
    tekniikkaData, lviData, sahkoData, tutkimusData,
    tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa,
  } = usePTS();

  const [hovered, setHovered] = useState(null);
  const [pinned,  setPinned]  = useState(null);
  const [open, setOpen]       = useState(() => window.innerWidth >= 768);
  const [containerW, setContainerW] = useState(800);
  const scrollRef  = useRef(null);
  const svgRef     = useRef(null);
  const wrapRef    = useRef(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(entries => setContainerW(entries[0].contentRect.width));
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const getScreenPos = (i, dotX, dotY) => {
    if (!svgRef.current) return null;
    const r = svgRef.current.getBoundingClientRect();
    return { index: i, sx: r.left + dotX, sy: r.top + dotY };
  };

  const handleDotEnter = (i, dotX, dotY) => {
    setHovered(getScreenPos(i, dotX, dotY));
  };

  const handleDotClick = (i, dotX, dotY) => {
    setPinned(p => p?.index === i ? null : getScreenPos(i, dotX, dotY));
  };

  // What to show in the tooltip: pinned takes priority over hover
  const activeTooltip = pinned ?? hovered;

  const events = useMemo(() => {
    const sources = [
      { data: tekniikkaData,  cat: 'Rakennetekniikka' },
      { data: lviData,        cat: 'LVI Järjestelmät' },
      { data: sahkoData,      cat: 'Sähköjärjestelmät' },
      { data: tutkimusData,   cat: 'Lisätutkimukset' },
    ];
    return years.map((year, yi) => {
      const tasks = [];
      sources.forEach(({ data, cat }) => {
        (data || []).forEach(section => {
          (section.items || []).forEach(item => {
            const val = parseFloat(item.values?.[yi]) || 0;
            if (val > 0 && item.label?.trim()) {
              tasks.push({ label: item.label, section: section.name, category: cat, color: CAT_COLOR[cat], cost: val });
            }
          });
        });
      });
      const total =
        (parseFloat(tekniikkaYhteensa[yi]) || 0) +
        (parseFloat(lviYhteensa[yi])      || 0) +
        (parseFloat(sahkoYhteensa[yi])    || 0) +
        (parseFloat(tutkimusYhteensa[yi]) || 0);
      return { year, tasks, total };
    });
  }, [years, tekniikkaData, lviData, sahkoData, tutkimusData,
      tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa]);

  const grandTotal = events.reduce((s, e) => s + e.total, 0);
  const maxTotal   = Math.max(...events.map(e => e.total), 1);
  const showYAxis  = containerW >= 480;
  const marginL    = showYAxis ? MARGIN_L : 8;
  const segW       = years.length
    ? Math.min(SEG_W_MAX, Math.floor((containerW - marginL - MARGIN_R) / years.length))
    : SEG_W_MAX;
  const svgW       = marginL + years.length * segW + MARGIN_R;
  const yForVal    = (v) => v > 0 ? Y_BOT - (v / maxTotal) * (Y_BOT - Y_TOP) : Y_BOT;
  const ticks      = niceTicks(maxTotal);
  const dotR       = segW < 50 ? 5 : segW < 80 ? 7 : DOT_R;
  const yearFontSz = segW < 40 ? 10 : segW < 80 ? 13 : segW < 110 ? 15 : 18.6;
  const curFontSz  = segW < 40 ? 12 : segW < 80 ? 15 : segW < 110 ? 18 : 23.2;
  const showCost   = segW >= 80;
  const shortYear  = segW < 45; // show "24" instead of "2024"

  const pts = years.map((_, i) => ({
    x: marginL + (i + 0.5) * segW,
    y: yForVal(events[i].total),
  }));

  const pathD = smoothPath(pts);

  useEffect(() => {
    if (!wrapRef.current || !years.length) return;
    const idx = years.indexOf(currentYear);
    if (idx >= 0) wrapRef.current.scrollLeft = Math.max(0, idx * segW - segW * 0.5);
  }, [years, currentYear, open, segW]);

  return (
    <div style={{ background: '#fff', borderRadius: 12, marginBottom: '1rem', overflow: 'hidden', border: '1px solid #d0e8d0' }}>

      {/* Header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.55rem 1rem', background: '#04aa00', borderBottom: open ? '1px solid #d0e8d0' : 'none', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.04em' }}>
            Toimenpideaikataulu
          </span>
          <span style={{ fontSize: '0.68rem', color: '#fff', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
            {fmt(grandTotal)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center' }}>
          {CAT_CONFIG.map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.67rem', color: '#fff' }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: '#fff', opacity: 0.85 }} />
              <span className="d-none d-xl-inline">{c.label}</span>
            </div>
          ))}
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <>
          <style>{`
            @keyframes pts-ring {
              0%,100% { opacity:.4; }
              50%      { opacity:.1; }
            }
            .pts-ring { animation: pts-ring 2.6s ease-in-out infinite; }
          `}</style>

          {/* Fixed tooltip */}
          {activeTooltip !== null && (() => {
            const ev    = events[activeTooltip.index];
            const isPinned = pinned?.index === activeTooltip.index;
            const ttW  = Math.min(TT_W, window.innerWidth - 16);
            const ttH  = ev.tasks.length > 0
              ? TT_HDR + ev.tasks.length * TT_ROW + TT_PAD
              : TT_HDR + 36;
            const above = activeTooltip.sy > window.innerHeight * 0.55;
            const left  = Math.max(8, Math.min(activeTooltip.sx - ttW / 2, window.innerWidth - ttW - 8));
            const top   = above ? activeTooltip.sy - ttH - 18 : activeTooltip.sy + 18;
            return (
              <div style={{
                position: 'fixed', left, top, width: ttW, zIndex: 9999,
                pointerEvents: isPinned ? 'auto' : 'none',
                background: '#fff',
                border: `1px solid ${isPinned ? '#04aa00' : '#c0dcc0'}`,
                borderRadius: 10,
                padding: '0.7rem 0.85rem',
                boxShadow: isPinned ? '0 4px 24px rgba(4,170,0,0.18)' : '0 4px 24px rgba(0,0,0,0.13)',
                fontFamily: "'Lato', sans-serif",
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem', borderBottom: '1px solid #e8f0e8', paddingBottom: '0.4rem' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a1a1a', fontFamily: "'Josefin Sans', sans-serif" }}>
                    {ev.year}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {ev.total > 0 && (
                      <span style={{ fontSize: '1rem', color: '#04aa00', fontWeight: 700 }}>
                        {fmtShort(ev.total)}
                      </span>
                    )}
                    {isPinned && (
                      <button
                        onClick={() => setPinned(null)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#aaa', fontSize: '1rem', lineHeight: 1, padding: '0 2px' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                {ev.tasks.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {ev.tasks.map((t, ti) => (
                      <div key={ti} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ width: 3, minHeight: 22, marginTop: 4, borderRadius: 2, background: t.color, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '1rem', color: '#1a1a1a', lineHeight: 1.35, fontWeight: 500 }}>
                            {t.label}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: t.color, fontWeight: 700, marginTop: 2 }}>
                            {fmtShort(t.cost)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '1rem', color: '#aaa', fontStyle: 'italic' }}>
                    Ei suunniteltuja töitä
                  </div>
                )}
              </div>
            );
          })()}

          <div ref={wrapRef} style={{ overflowX: 'auto', background: '#f8faf8' }}>
            <div ref={scrollRef} style={{ position: 'relative', width: svgW, margin: '0 auto' }}>
              <svg ref={svgRef} width={svgW} height={SVG_H} style={{ display: 'block', overflow: 'visible' }}>
                <defs>
                  <filter id="pts-gl" x="-20%" y="-300%" width="140%" height="700%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b" />
                    <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                  <filter id="pts-gd" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b" />
                    <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                </defs>

                <rect width={svgW} height={SVG_H} fill="#f8faf8" />

                {/* Y-axis tick lines + labels */}
                {ticks.map(v => {
                  const y = yForVal(v);
                  return (
                    <g key={v}>
                      <line x1={marginL} y1={y} x2={svgW} y2={y}
                        stroke="#e0eae0" strokeWidth={1} strokeDasharray="4 4" />
                      {showYAxis && (
                        <text x={marginL - 8} y={y + 4} textAnchor="end"
                          fontSize={11} fill="#888" fontFamily="'Lato', sans-serif"
                          style={{ userSelect: 'none' }}>
                          {fmtShort(v)}
                        </text>
                      )}
                    </g>
                  );
                })}
                {/* Y-axis baseline at Y_BOT (zero line) */}
                <line x1={marginL} y1={Y_BOT} x2={svgW} y2={Y_BOT}
                  stroke="#c8d8c8" strokeWidth={1} />
                {showYAxis && (
                  <text x={marginL - 8} y={Y_BOT + 4} textAnchor="end"
                    fontSize={11} fill="#bbb" fontFamily="'Lato', sans-serif"
                    style={{ userSelect: 'none' }}>
                    0
                  </text>
                )}
                {/* Vertical axis line */}
                <line x1={marginL} y1={Y_TOP - 10} x2={marginL} y2={Y_BOT}
                  stroke="#c8d8c8" strokeWidth={1} />

                {/* Road line — subtle shadow */}
                <path d={pathD} fill="none" stroke="#04aa00" strokeWidth={6}
                  strokeLinecap="round" opacity={0.12} filter="url(#pts-gl)" />
                {/* Road line — main */}
                <path d={pathD} fill="none" stroke="#04aa00" strokeWidth={2.5}
                  strokeLinecap="round" opacity={0.85} />

                {/* Year dots */}
                {pts.map(({ x, y }, i) => {
                  const ev      = events[i];
                  const isCur   = ev.year === currentYear;
                  const isHov   = hovered?.index === i;
                  const isPin   = pinned?.index === i;

                  return (
                    <g key={ev.year}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => handleDotEnter(i, x, y)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => handleDotClick(i, x, y)}
                    >
                      {/* Pulsing ring for current year */}
                      {isCur && (
                        <circle className="pts-ring" cx={x} cy={y}
                          r={dotR + 9} fill="none" stroke="#04aa00" strokeWidth={2} />
                      )}

                      {/* Dot */}
                      <circle
                        cx={x} cy={y}
                        r={isHov || isCur || isPin ? dotR + 3 : dotR}
                        fill={isCur || isPin ? '#04aa00' : (ev.total > 0 ? '#fff' : '#f0f4f0')}
                        stroke={isCur || isPin ? '#027000' : (ev.total > 0 ? '#04aa00' : '#c0d8c0')}
                        strokeWidth={isCur || isPin ? 0 : 2}
                        filter={isHov || isCur || isPin ? 'url(#pts-gd)' : undefined}
                      />

                      {/* White pip for current year */}
                      {isCur && <circle cx={x} cy={y} r={3.5} fill="#fff" />}

                      {/* Year label — always above */}
                      <text
                        x={x} y={y - dotR - 10}
                        textAnchor="middle"
                        fontSize={isCur ? curFontSz : yearFontSz}
                        fontWeight={isCur ? 700 : 500}
                        fill={isCur ? '#027000' : (isHov ? '#04aa00' : '#555')}
                        fontFamily="'Josefin Sans', sans-serif"
                        style={{ userSelect: 'none' }}
                      >
                        {shortYear ? String(ev.year).slice(2) : ev.year}
                      </text>

                      {/* Total sum — always below */}
                      {ev.total > 0 && showCost && (
                        <text
                          x={x} y={y + dotR + 17}
                          textAnchor="middle"
                          fontSize={17}
                          fontWeight={600}
                          fill={isCur ? '#04aa00' : '#888'}
                          fontFamily="'Lato', sans-serif"
                          style={{ userSelect: 'none' }}
                        >
                          {fmtShort(ev.total)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
